import { ChangeDetectionStrategy, Component } from '@angular/core';

import { RevealDirective } from '@ledgerly/common/browser';

/**
 * The setup, in three lines.
 *
 * These three steps used to sit in the hero, under the promise and above the
 * store badges. They were doing two jobs there: they pushed the badges below
 * the fold, and they answered "how does this work" in a place where the reader
 * was still asking "what is this".
 *
 * They are the shortest honest description of the product, so they get their
 * own section directly under the hero, before anything is asked of the reader.
 * The ids are unchanged, so the four translations carry over untouched.
 *
 * No bank connection, no import and no automatic categorisation. Step three
 * says "record", and it means by hand. See CLAUDE.md.
 */
@Component({
  selector: 'app-steps',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RevealDirective],
  template: `
    <section id="how" class="bg-ground py-[112px]">
      <div class="mx-auto max-w-[1180px] px-8">
        <p
          class="text-kicker uppercase text-accent reveal"
          appReveal
          i18n="@@stepsKicker"
        >
          Setting up
        </p>
        <h2
          class="mt-kicker-gap max-w-[22ch] text-headline text-ink text-balance reveal"
          appReveal
          i18n="@@stepsHeadline"
        >
          Three things, then you are set up.
        </h2>
        <p
          class="mt-8 max-w-[58ch] text-body text-ink-2 reveal"
          appReveal
          i18n="@@stepsIntro"
        >
          It takes a few minutes once. After that you add what you spend as you
          spend it.
        </p>

        <ol class="mt-[56px] grid gap-12 md:grid-cols-3 md:gap-16">
          @for (step of steps; track step.n) {
          <li class="reveal" [appReveal]="$index * 80">
            <p class="text-kicker text-accent tabular-nums">{{ step.n }}</p>
            <p class="mt-kicker-gap max-w-[26ch] text-body text-ink">
              {{ step.text }}
            </p>
          </li>
          }
        </ol>
      </div>
    </section>
  `,
})
export class StepsComponent {
  protected readonly steps = [
    { n: '01', text: $localize`:@@heroStep1:Say what comes in each month.` },
    {
      n: '02',
      text: $localize`:@@heroStep2:Set aside the part you want to keep.`,
    },
    {
      n: '03',
      text: $localize`:@@heroStep3:Give the rest a job, then just record.`,
    },
  ];
}
